import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Award, CheckCircle2, Clock, XCircle } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'dept_leader', label: 'Department Leader', hint: 'Lead a ministry department (Youth, Music, Missions, etc.)' },
  { value: 'bishop', label: 'Bishop', hint: 'Ecclesiastical oversight' },
  { value: 'secretary', label: 'Jurisdictional Secretary', hint: 'Records, minutes, official documents' },
  { value: 'cfo', label: 'Chief Financial Officer', hint: 'Financial stewardship and reporting' },
  { value: 'member', label: 'Verified Member', hint: 'Basic member portal access' },
];

const STATUS_ICONS = {
  pending: <Clock className="w-4 h-4 text-yellow-600" />,
  approved: <CheckCircle2 className="w-4 h-4 text-green-600" />,
  rejected: <XCircle className="w-4 h-4 text-red-500" />,
};

export default function ClaimPosition() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [claims, setClaims] = useState([]);
  const [form, setForm] = useState({
    requested_role: 'dept_leader', department_id: '', district_id: '',
    position_title: '', experience: '', contact_phone: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);

  const load = () => {
    api.get('/site/departments').then(r => setDepartments(r.data)).catch(() => {});
    api.get('/site/districts').then(r => setDistricts(r.data)).catch(() => {});
    api.get('/role-claims/mine').then(r => setClaims(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  const submit = async () => {
    if (!form.requested_role) { toast.error('Choose a role to claim'); return; }
    if (form.requested_role === 'dept_leader' && !form.department_id) {
      toast.error('Choose the department you want to lead'); return;
    }
    setBusy(true);
    try {
      await api.post('/role-claims', form);
      toast.success('Claim submitted for admin review');
      setForm({ ...form, position_title: '', experience: '', contact_phone: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally { setBusy(false); }
  };

  if (loading || !user) return <div className="py-24 text-center">Loading…</div>;

  const hasPending = claims.some(c => c.status === 'pending');

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-12">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-8">
          <div className="overline mb-1">Ministry Portal</div>
          <h1 className="font-serif text-4xl text-royal font-bold flex items-center gap-3">
            <Award className="w-8 h-8 text-gold" /> Claim a Position
          </h1>
          <p className="text-charcoal-muted text-sm mt-2 max-w-2xl">
            Request a ministry role in the jurisdiction. Your submission will be reviewed by an administrator.
            Approved claims automatically update your account and grant portal access to the corresponding tools.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 bg-white border rounded-lg p-6" data-testid="claim-form-card">
            <h2 className="font-serif text-xl text-royal font-bold mb-4">New Claim</h2>

            {hasPending && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                You already have a claim awaiting review. You can submit another once it's processed.
              </div>
            )}

            <label className="block mb-3">
              <span className="text-sm font-semibold text-royal">Position / Role</span>
              <select
                data-testid="claim-role-select"
                value={form.requested_role}
                onChange={(e) => setForm({ ...form, requested_role: e.target.value, department_id: '' })}
                className="mt-1 w-full px-3 py-2 border rounded"
              >
                {ROLE_OPTIONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <div className="text-xs text-charcoal-muted mt-1">
                {ROLE_OPTIONS.find(r => r.value === form.requested_role)?.hint}
              </div>
            </label>

            {form.requested_role === 'dept_leader' && (
              <label className="block mb-3">
                <span className="text-sm font-semibold text-royal">Department</span>
                <select
                  data-testid="claim-department-select"
                  value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border rounded"
                >
                  <option value="">Select a department…</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </label>
            )}

            <label className="block mb-3">
              <span className="text-sm font-semibold text-royal">District (optional)</span>
              <select
                data-testid="claim-district-select"
                value={form.district_id}
                onChange={(e) => setForm({ ...form, district_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 border rounded"
              >
                <option value="">None / Statewide</option>
                {districts.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </label>

            <label className="block mb-3">
              <span className="text-sm font-semibold text-royal">Position Title (optional)</span>
              <input
                data-testid="claim-title-input"
                type="text"
                value={form.position_title}
                onChange={(e) => setForm({ ...form, position_title: e.target.value })}
                placeholder='e.g. "Youth Department President"'
                className="mt-1 w-full px-3 py-2 border rounded"
              />
            </label>

            <label className="block mb-3">
              <span className="text-sm font-semibold text-royal">Contact Phone (optional)</span>
              <input
                data-testid="claim-phone-input"
                type="text"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="mt-1 w-full px-3 py-2 border rounded"
              />
            </label>

            <label className="block mb-4">
              <span className="text-sm font-semibold text-royal">Experience / Notes for Admin</span>
              <textarea
                data-testid="claim-experience-input"
                rows="5"
                value={form.experience}
                onChange={(e) => setForm({ ...form, experience: e.target.value })}
                placeholder="Share your current ministry involvement, prior roles, and why you want this position."
                className="mt-1 w-full px-3 py-2 border rounded"
              />
            </label>

            <button
              onClick={submit}
              disabled={busy || hasPending}
              data-testid="claim-submit-btn"
              className="btn-royal"
            >
              {busy ? 'Submitting…' : 'Submit for Review'}
            </button>
          </div>

          <div className="md:col-span-2">
            <h2 className="font-serif text-xl text-royal font-bold mb-4">My Claims</h2>
            <div className="space-y-3">
              {claims.length === 0 && (
                <div className="bg-white border rounded p-4 text-sm text-charcoal-muted">
                  You haven't submitted any claims yet.
                </div>
              )}
              {claims.map(c => (
                <div key={c.id} data-testid={`claim-item-${c.id}`} className="bg-white border rounded p-4">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-serif text-royal font-bold">
                        {ROLE_OPTIONS.find(r => r.value === c.requested_role)?.label || c.requested_role}
                      </div>
                      {c.position_title && <div className="text-xs text-charcoal-muted">{c.position_title}</div>}
                    </div>
                    <div className={`flex items-center gap-1 text-xs uppercase font-bold px-2 py-1 rounded ${
                      c.status === 'approved' ? 'bg-green-100 text-green-700' :
                      c.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {STATUS_ICONS[c.status]} {c.status}
                    </div>
                  </div>
                  <div className="text-[11px] text-charcoal-muted mt-2">
                    Submitted {new Date(c.created_at).toLocaleDateString()}
                  </div>
                  {c.admin_note && (
                    <div className="mt-2 text-xs bg-gray-50 border rounded p-2">
                      <span className="font-semibold text-royal">Admin note:</span> {c.admin_note}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
